import {PageHeader} from '../ui/PageHeader';
import { DateOnlyInput } from '../components/DateOnlyInput';
import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { alphaApi as api } from './alpha-request';
import { ErrorState, Loading } from '../ui/State';
export function PaymentPlanPortalAlpha() {
  const { id } = useParams(),
    [data, setData] = useState<any>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false),
    root = `/api/portal/payment-plans/${id}`;
  const load = () => api(root).then(setData);
  useEffect(() => {
    void load().catch(setError);
  }, [id]);
  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setBusy(true);
    const f = new FormData(e.currentTarget);
    try {
      await api(root, {
        method: 'POST',
        body: JSON.stringify({ ...Object.fromEntries(f), idempotencyKey: crypto.randomUUID() }),
      });
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <PageHeader title="Payment arrangements" context={data?`${data.reference} · Balance £${data.balance} · Disputed £${data.disputed}`:undefined}/>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!data && !error ? (
        <Loading />
      ) : !data ? null : (
        <>
          {data.arrangement && (
            <section className="panel">
              <h2>Your approved arrangement</h2>
              <p>
                £{data.arrangement.instalmentAmount} · {data.arrangement.frequency} ·{' '}
                {data.arrangement.status}
              </p>
              <p>
                Expected to date £{data.arrangement.expected}; receipts recorded £
                {data.arrangement.received}.
              </p>
              {data.arrangement.potentiallyMissed && (
                <p>
                  Recorded receipts are below the expected amount. Contact your manager if a payment
                  is missing or disputed.
                </p>
              )}
            </section>
          )}
          <h2>Your requests</h2>
          {!data.requests.length&&<p>No arrangement requests were returned.</p>}
          {data.requests.map((q: any) => (
            <section className="panel" key={q.id}>
              <p>
                {q.status} · £{q.proposal.instalmentAmount} {q.proposal.frequency} towards £
                {q.proposal.coveredAmount}
              </p>
              <p>{q.reason}</p>
            </section>
          ))}
          {!data.requests.some((q: any) => q.status === 'Pending') && (
            <section className="panel">
              <h2>Propose an arrangement</h2>
              <p>
                This is a request for review. It does not change your account or collect a payment.
              </p>
              <form className="form portal-payment-plan-form" onSubmit={save}>
                <label>
                  Amount to cover (£)
                  <input
                    name="coveredAmount"
                    required
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]{1,2})?"
                  />
                </label>
                <label>
                  Each instalment (£)
                  <input
                    name="instalmentAmount"
                    required
                    inputMode="decimal"
                    pattern="[0-9]+(\.[0-9]{1,2})?"
                  />
                </label>
                <label>
                  Frequency
                  <select name="frequency">
                    <option>Monthly</option>
                    <option>Weekly</option>
                    <option>Fortnightly</option>
                    <option>Quarterly</option>
                  </select>
                </label>
                {[
                  ['startsOn', 'Arrangement starts'],
                  ['firstPaymentOn', 'First payment'],
                  ['completionOn', 'Final payment'],
                ].map(([name, label]) => (
                  <label key={name}>
                    {label}
                    <DateOnlyInput name={name!} required />
                  </label>
                ))}
                <label>
                  Your proposal
                  <textarea name="reason" minLength={5} maxLength={2000} required />
                </label>
                <button disabled={busy}>{busy ? 'Saving…' : 'Submit request'}</button>
              </form>
            </section>
          )}
        </>
      )}
    </>
  );
}
export function PaymentPlanReviewAlpha() {
  const [q] = useSearchParams(),
    building = q.get('building'),
    [items, setItems] = useState<any[]>(),
    [error, setError] = useState<unknown>(),
    [busy, setBusy] = useState(false);
  const load = () =>
    api<{ items: any[] }>(`/api/alpha/payment-plans?building=${building}`).then((r) =>
      setItems(r.items),
    );
  useEffect(() => {
    if (building) void load().catch(setError);
  }, [building]);
  async function save(e: FormEvent<HTMLFormElement>, q: any) {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    try {
      await api(`/api/alpha/payment-plans/${q.id}/decision`, {
        method: 'POST',
        body: JSON.stringify({
          decision: f.get('decision'),
          reason: f.get('reason'),
          expectedVersion: q.version,
          idempotencyKey: crypto.randomUUID(),
        }),
      });
      await load();
      setError(undefined);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <h1>Resident payment-plan requests</h1>
      {Boolean(error) && <ErrorState error={error} />}{' '}
      {!building ? (
        <p>Open this page in a building.</p>
      ) : !items && !error ? (
        <Loading />
      ) : !items ? null : !items.length ? (
        <p>No requests yet.</p>
      ) : (
        items.map((q) => (
          <section className="panel" key={q.id}>
            <h2>
              {q.display_name} · {q.account_reference}
            </h2>
            <p>
              {q.status} · £{q.proposal.instalmentAmount} {q.proposal.frequency} towards £
              {q.proposal.coveredAmount}
            </p>
            <p>
              Starts {q.proposal.startsOn}; first payment {q.proposal.firstPaymentOn}; completion{' '}
              {q.proposal.completionOn}
            </p>
            <p>{q.proposal.reason}</p>
            {q.status === 'Pending' && (
              <form className="form" onSubmit={(e) => void save(e, q)}>
                <label>
                  Decision
                  <select name="decision">
                    <option>Approved</option>
                    <option>Rejected</option>
                  </select>
                </label>
                <label>
                  Review notes
                  <textarea name="reason" required minLength={5} maxLength={2000} />
                </label>
                <button disabled={busy}>Save decision</button>
              </form>
            )}
          </section>
        ))
      )}
    </>
  );
}
